import { toMap } from '../../utils/arrayUtils';
import { getAircraft } from '../Aircrafts/AircraftsDatasource';
import { type FlightLeg } from '../FlightLegs/FlightLeg';
import { isLegFinished } from '../FlightLegs/FlightLeg';
import { getDbSyncer } from '../LocalDB/DbSyncer';
import { getDb } from '../LocalDB/LocalDB';
import { getInitialCountableChange } from './CountableChange';
import { type CountableChange } from './types';
import { COUNTABLE_CHANGE_CONSUMPTION, COUNTABLE_CHANGE_CORRECTION, COUNTABLE_CHANGE_UPLIFT } from './types';

export const getCountableChange = async (id: string): Promise<CountableChange | null> => {
    return (await getDb().countableChanges.get(id)) || null;
};

export const getCountableChanges = async (ids: string[]): Promise<Array<CountableChange | undefined>> => {
    return getDb().countableChanges.bulkGet(ids);
};

export const getCountableChangesForLeg = async (legId: string): Promise<CountableChange[]> => {
    return getDb().countableChanges.where({ legId }).toArray();
};

export const getLastCountableChangeForLeg = async (leg: FlightLeg): Promise<CountableChange> => {
    const maybeCountableChange = isLegFinished(leg)
        ? await getCountableChange(leg.consumptionCountableChangeId)
        : await getCountableChange(leg.upliftCountableChangeId);
    if (maybeCountableChange) {
        return maybeCountableChange;
    }
    const aircraft = await getAircraft(leg.aircraftId);
    if (!aircraft) {
        throw new Error('Trying to get last countable change for non-existing aircraft');
    }
    return getInitialCountableChange(aircraft, leg.editableByUserIds);
};

/**
 * The aircraft's newest countable change, with `before` the newest one before that time. A leg's CCs count at its
 * block-off - an open leg's after everything, its timeOut is only a midnight placeholder - corrections and technical
 * uplifts at their createdAt. CCs of a deleted leg or flight and CCs their leg no longer points to don't count.
 */
export const findLastCountableChange = async (
    aircraftId: string,
    before?: Date
): Promise<CountableChange | undefined> => {
    const db = getDb();
    const [changes, legs, flights] = await Promise.all([
        db.countableChanges.where({ aircraftId }).toArray(),
        db.legs.where({ aircraftId }).toArray(),
        db.flights.where({ aircraftId }).toArray(),
    ]);
    const legsMap = toMap(legs);
    const flightsMap = toMap(flights);
    let last: { countableChange: CountableChange; time: number } | undefined;
    for (const countableChange of changes) {
        const leg = countableChange.legId ? legsMap.get(countableChange.legId) : undefined;
        const flight = countableChange.flightId ? flightsMap.get(countableChange.flightId) : undefined;
        const isLegsCurrent =
            !leg ||
            countableChange.id === leg.upliftCountableChangeId ||
            (isLegFinished(leg) && countableChange.id === leg.consumptionCountableChangeId);
        if (countableChange.deleted || leg?.deleted || flight?.deleted || !isLegsCurrent) {
            continue;
        }
        const time = leg
            ? isLegFinished(leg)
                ? leg.timeOut.getTime()
                : Number.POSITIVE_INFINITY
            : countableChange.createdAt.getTime();
        if (before && time >= before.getTime()) {
            continue;
        }
        if (
            !last ||
            time > last.time ||
            (time === last.time &&
                countableChangeTypeSuccession.indexOf(countableChange.type) >
                    countableChangeTypeSuccession.indexOf(last.countableChange.type))
        ) {
            last = { countableChange, time };
        }
    }

    return last?.countableChange;
};

export const getLastCountableChangeForAircraft = async (aircraftId: string): Promise<CountableChange> => {
    const last = await findLastCountableChange(aircraftId);
    if (last) {
        return last;
    }
    const aircraft = await getAircraft(aircraftId);
    if (!aircraft) {
        throw new Error('Trying to get last countable change for non-existing aircraft');
    }
    return getInitialCountableChange(aircraft);
};

export const putCountableChange = async (countableChange: CountableChange): Promise<void> => {
    let db = getDb();
    await db.countableChanges.put(countableChange);
    getDbSyncer().markToSync(db.countableChanges, countableChange.id);
};

const countableChangeTypeSuccession = [
    COUNTABLE_CHANGE_CORRECTION,
    COUNTABLE_CHANGE_UPLIFT,
    COUNTABLE_CHANGE_CONSUMPTION,
];
